import {
  dayKeyToMs,
  escapeHtml,
  formatCompactCount,
  formatCount,
  formatDate,
  formatDuration,
  formatHour,
  formatPercent,
  formatTime,
  josa,
  weekdayName,
} from '../format';
import { percentWith, withinText } from '../report/text';
import {
  CONVERSATION_GAP_MINUTES,
  LATE_NIGHT_HOURS,
  MIN_REPLIES_FOR_SPEED,
  QUICK_REPLY_MINUTES,
  SHORT_REPLY_MAX_CHARS,
} from '../stats/constants';
import type { ChatStats, MessageKind, MessageSnippet, PersonStats } from '../types';
import { barRowsMarkup, columnChartMarkup, donutMarkup, type BarRow } from './charts';
import { formatAverage, formatOneDecimal, formatPeriod, limitItems, peopleByMessages, personColor, type VisibleSplit } from './format-ui';
import { buildHeatmap, HEATMAP_WEEKS, heatmapMarkup } from './heatmap';

/**
 * "자세히 보기" statistic cards (spec §6.2-4). Every chart has its numbers in
 * visible text or a screen-reader list. Names, emoji, words and snippets come
 * from the chat, so everything is escaped.
 */

/** Per-person lists show at most this many people: the busiest ones, ordered by each card's metric. */
export const MAX_PEOPLE_IN_CARD = 6;
const OTHERS_COLOR = '#c9c3d6';

const KIND_LABELS: Readonly<Record<MessageKind, string>> = {
  text: '',
  photo: '사진',
  video: '동영상',
  emoticon: '이모티콘',
  voice: '음성메시지',
  file: '파일',
  call: '통화',
  transfer: '송금',
  deleted: '삭제된 메시지',
  hidden: '가려진 메시지',
  media: '공유한 항목',
};

interface CardContent {
  readonly id: string;
  readonly title: string;
  readonly lead?: string;
  readonly body: string;
  readonly footnote?: string;
  readonly wide?: boolean;
}

function card(content: CardContent): string {
  return `
    <article class="krc-card krc-stat${content.wide ? ' is-wide' : ''}" id="krc-stat-${content.id}" aria-labelledby="krc-stat-${content.id}-title">
      <h3 id="krc-stat-${content.id}-title">${escapeHtml(content.title)}</h3>
      ${content.lead ? `<p class="krc-stat-lead">${escapeHtml(content.lead)}</p>` : ''}
      ${content.body}
      ${content.footnote ? `<p class="krc-footnote">${escapeHtml(content.footnote)}</p>` : ''}
    </article>`;
}

function emptyNote(text: string): string {
  return `<p class="krc-empty">${escapeHtml(text)}</p>`;
}

function personBadge(person: PersonStats): string | undefined {
  return person.isMe ? '나' : undefined;
}

function nameOf(stats: ChatStats, index: number): string {
  return stats.people.find((person) => person.index === index)?.name ?? '';
}

function hiddenPeopleNote(hidden: number): string {
  return hidden > 0 ? `<p class="krc-more-people">메시지가 많은 ${MAX_PEOPLE_IN_CARD}명만 보여 드려요 (외 ${formatCount(hidden)}명).</p>` : '';
}

type PersonOrder = (a: PersonStats, b: PersonStats) => number;

/**
 * The people a per-person card lists: the MAX_PEOPLE_IN_CARD busiest, which is
 * what hiddenPeopleNote says, ordered by the card's own metric. Picking by
 * messages first also keeps a member with one or two messages from topping a
 * rate card (100% after midnight) in a big group.
 */
function busiestPeople(people: readonly PersonStats[], order: PersonOrder): VisibleSplit<PersonStats> {
  const { shown, hidden } = limitItems(peopleByMessages(people), MAX_PEOPLE_IN_CARD);
  return { shown: [...shown].sort((a, b) => order(a, b) || a.index - b.index), hidden };
}

/** First person by `order` across everyone (for leads about the whole room). */
function leaderBy(people: readonly PersonStats[], order: PersonOrder): PersonStats | undefined {
  return [...people].sort((a, b) => order(a, b) || a.index - b.index)[0];
}

/**
 * Everyone whose value prints the same as the best one, in person order.
 * Superlative leads ("가장 빠른", "최장 기록") compare what the bars print,
 * so values that read the same are a tie and nobody is crowned over the rest.
 */
function sharedBest(
  people: readonly PersonStats[],
  value: (person: PersonStats) => number,
  printed: (value: number) => string,
  best: 'lowest' | 'highest',
): PersonStats[] {
  if (!people.length) return [];
  const better = (a: number, b: number) => (best === 'lowest' ? a < b : a > b);
  const top = people.reduce((leader, person) => (better(value(person), value(leader)) ? person : leader));
  const label = printed(value(top));
  return people.filter((person) => printed(value(person)) === label);
}

/** "서진·민혁", or "서진·민혁 등 4명" when many share a lead (as the subject comments print it). */
function sharedNames(people: readonly PersonStats[]): string {
  const names = people.map((person) => person.name);
  return names.length <= 3 ? names.join('·') : `${names.slice(0, 2).join('·')} 등 ${formatCount(names.length)}명`;
}

function argMax(values: readonly number[]): number {
  let best = 0;
  for (let index = 1; index < values.length; index++) if (values[index]! > values[best]!) best = index;
  return best;
}

function peakHour(person: PersonStats): number | null {
  return person.hourly.some((value) => value > 0) ? argMax(person.hourly) : null;
}

// ---------------------------------------------------------------------------

function talkShareCard(stats: ChatStats): string {
  const ranked = peopleByMessages(stats.people);
  const { shown, hidden } = limitItems(ranked, 5);
  const others = ranked.slice(shown.length).reduce((sum, person) => sum + person.messages, 0);
  const segments = [
    ...shown.map((person) => ({ value: person.messages, color: personColor(person.index) })),
    ...(others > 0 ? [{ value: others, color: OTHERS_COLOR }] : []),
  ];
  const legendItems = shown.map((person) => `
    <li><span class="krc-dot" style="background:${personColor(person.index)}" aria-hidden="true"></span><span class="krc-legend-name">${escapeHtml(person.name)}${person.isMe ? ' <span class="krc-me-badge">나</span>' : ''}</span><strong>${formatPercent(person.share)}</strong><small>${formatCount(person.messages)}개</small></li>`);
  if (hidden > 0) {
    legendItems.push(`<li><span class="krc-dot" style="background:${OTHERS_COLOR}" aria-hidden="true"></span><span class="krc-legend-name">그 외 ${formatCount(hidden)}명</span><strong>${formatPercent(others / Math.max(1, stats.totals.messages))}</strong><small>${formatCount(others)}개</small></li>`);
  }
  const top = shown[0];
  return card({
    id: 'share',
    title: '누가 말이 많을까',
    lead: top ? `${josa(top.name, '이/가')} 전체 메시지의 ${percentWith(top.share, '을/를')} 보냈어요.` : undefined,
    body: `<div class="krc-share-chart">${donutMarkup(segments, { value: formatCompactCount(stats.totals.messages), label: '메시지' })}<ul class="krc-legend">${legendItems.join('')}</ul></div>`,
    footnote: '사진·이모티콘 같은 미디어도 한 개로 셌어요.',
  });
}

function initiativeCard(stats: ChatStats): string {
  const byStarts: PersonOrder = (a, b) => b.starts - a.starts;
  const { shown, hidden } = busiestPeople(stats.people, byStarts);
  const rows: BarRow[] = shown.map((person) => ({
    label: person.name,
    value: person.startShare,
    valueText: formatPercent(person.startShare),
    color: personColor(person.index),
    note: `${formatCount(person.starts)}번 먼저 말을 걸었어요`,
    badge: personBadge(person),
  }));
  const top = leaderBy(stats.people, byStarts);
  return card({
    id: 'initiative',
    title: '누가 먼저 말을 걸까',
    lead: top && top.starts > 0
      ? `대화 ${formatCount(stats.totals.conversations)}번 중 ${formatCount(top.starts)}번을 ${josa(top.name, '이/가')} 열었어요.`
      : undefined,
    body: barRowsMarkup(rows, { max: 1 }) + hiddenPeopleNote(hidden),
    footnote: `${formatDuration(CONVERSATION_GAP_MINUTES)} 이상 조용하다가 처음 보낸 메시지를 선톡으로 셌어요.`,
  });
}

/**
 * The 답장 속도 lead, about everyone measured in the room. It names one
 * fastest replier only when two or more were measured and no one else's bar
 * prints the same time ("보통 3분"); shared times read 둘 다 / 다들 / 나란히,
 * like the subject comment, and a lone measured person gets no "가장".
 */
function replyLead(measured: readonly PersonStats[]): string | undefined {
  const median = (person: PersonStats) => person.medianReplyMinutes!;
  const fastest = sharedBest(measured, median, formatDuration, 'lowest');
  const first = fastest[0];
  if (!first) return undefined;
  const usually = `보통 ${withinText(median(first))} 답해요.`;
  if (measured.length === 1) return `${josa(first.name, '은/는')} ${usually}`;
  if (fastest.length === 1) return `가장 빠른 답장러는 ${first.name}. ${usually}`;
  if (fastest.length === measured.length) return `${measured.length === 2 ? '둘 다' : '다들'} ${usually}`;
  return `가장 빠른 답장러는 ${sharedNames(fastest)}. 나란히 ${usually}`;
}

function replyCard(stats: ChatStats): string {
  const isMeasured = (person: PersonStats) => person.medianReplyMinutes !== null;
  const byMedian: PersonOrder = (a, b) => a.medianReplyMinutes! - b.medianReplyMinutes!;
  // The busiest people get a bar each, or a mention below when they replied too rarely to measure.
  const { shown: busiest, hidden } = limitItems(peopleByMessages(stats.people), MAX_PEOPLE_IN_CARD);
  const measured = busiest.filter(isMeasured).sort((a, b) => byMedian(a, b) || a.index - b.index);
  const unmeasured = busiest.filter((person) => !isMeasured(person));
  const rows: BarRow[] = measured.map((person) => ({
    label: person.name,
    value: person.medianReplyMinutes!,
    valueText: `보통 ${formatDuration(person.medianReplyMinutes!)}`,
    color: personColor(person.index),
    note: `${QUICK_REPLY_MINUTES}분 안팎에 보낸 답장 ${formatPercent(person.quickReplyRate ?? 0)}`,
    badge: personBadge(person),
  }));
  // The lead is about the whole room; it stays away when no bar is shown, so it never sits above "재지 못했어요".
  const lead = rows.length ? replyLead(stats.people.filter(isMeasured)) : undefined;
  const missing = unmeasured.length
    ? `<p class="krc-more-people">답장이 ${MIN_REPLIES_FOR_SPEED}개보다 적어서 재지 않았어요: ${escapeHtml(unmeasured.map((person) => person.name).join(', '))}</p>`
    : '';
  return card({
    id: 'reply',
    title: '답장 속도',
    lead,
    body: (rows.length ? barRowsMarkup(rows, { scale: 'log' }) + '<p class="krc-chart-caption">막대가 짧을수록 빨라요.</p>' : emptyNote('답장이 아직 적어서 속도를 재지 못했어요.')) + hiddenPeopleNote(hidden) + missing,
    footnote: '카톡은 분 단위로 기록돼서 답장 시간은 대략이에요. 보통 = 답장 시간의 중앙값.',
  });
}

const HOUR_TICKS = Array.from({ length: 24 }, (_, hour) => (hour % 6 === 0 ? `${hour}시` : null));

function hourlyCard(stats: ChatStats): string {
  const ranked = peopleByMessages(stats.people).slice(0, stats.roomKind === 'pair' ? 2 : 3);
  const peaks = ranked
    .map((person) => ({ person, hour: peakHour(person) }))
    .filter((item): item is { person: PersonStats; hour: number } => item.hour !== null);
  const peakText = peaks.length
    ? `${peaks.map(({ person, hour }) => `${person.name} ${formatHour(hour)}`).join(' · ')}`
    : '';
  return card({
    id: 'hourly',
    title: '몇 시에 제일 수다스러울까',
    lead: `가장 바쁜 시간은 ${josa(formatHour(stats.busiestHour), '이에요/예요')}.`,
    body: columnChartMarkup(stats.hourly, {
      labels: Array.from({ length: 24 }, (_, hour) => formatHour(hour)),
      ticks: HOUR_TICKS,
      highlight: stats.busiestHour,
      summary: peakText ? `사람별로 가장 많이 보낸 시간: ${peakText}` : '시간대별 메시지 수예요.',
      unit: '개',
      className: 'is-hourly',
    }),
    wide: true,
  });
}

/** "0시~4시 59분" from LATE_NIGHT_HOURS. */
export function lateNightRangeLabel(hours: readonly number[] = LATE_NIGHT_HOURS): string {
  const first = Math.min(...hours);
  const last = Math.max(...hours);
  return `${first}시~${last}시 59분`;
}

function lateNightCard(stats: ChatStats): string {
  const { shown, hidden } = busiestPeople(stats.people, (a, b) => b.lateNightRate - a.lateNightRate);
  const rows: BarRow[] = shown.map((person) => ({
    label: person.name,
    value: person.lateNightRate,
    valueText: formatPercent(person.lateNightRate),
    color: personColor(person.index),
    badge: personBadge(person),
  }));
  return card({
    id: 'late-night',
    title: '자정 넘어서도 깨어 있는 사람',
    lead: `메시지의 ${percentWith(stats.lateNightRate, '이/가')} 새벽(${lateNightRangeLabel()})에 오갔어요.`,
    body: barRowsMarkup(rows) + hiddenPeopleNote(hidden),
    footnote: `사람마다 자기 메시지 중 새벽(${lateNightRangeLabel()})에 보낸 비율이에요.`,
  });
}

const WEEKDAY_TICKS = ['일', '월', '화', '수', '목', '금', '토'];

function weekdayCard(stats: ChatStats): string {
  const busiest = argMax(stats.weekday);
  const total = stats.weekday.reduce((sum, value) => sum + value, 0);
  return card({
    id: 'weekday',
    title: '요일별 대화량',
    lead: total > 0 ? `가장 수다스러운 요일은 ${josa(weekdayName(busiest), '이에요/예요')}.` : undefined,
    body: columnChartMarkup(stats.weekday, {
      labels: WEEKDAY_TICKS.map((_, index) => weekdayName(index)),
      ticks: WEEKDAY_TICKS,
      highlight: total > 0 ? busiest : null,
      summary: total > 0 ? `${weekdayName(busiest)}에 전체 메시지의 ${percentWith(stats.weekday[busiest]! / total, '이/가')} 몰려 있어요.` : '요일별 메시지 수예요.',
      unit: '개',
      className: 'is-weekday',
    }),
  });
}

function emojiCard(stats: ChatStats): string {
  const top = stats.topEmojis.slice(0, 5);
  const totals = stats.people.reduce(
    (sum, person) => ({
      emoticon: sum.emoticon + person.media.emoticon,
      photo: sum.photo + person.media.photo,
      voice: sum.voice + person.media.voice,
      link: sum.link + person.media.link,
    }),
    { emoticon: 0, photo: 0, voice: 0, link: 0 },
  );
  const favorites = peopleByMessages(stats.people)
    .slice(0, MAX_PEOPLE_IN_CARD)
    .filter((person) => person.emojis.length > 0)
    .map((person) => `<li><span class="krc-dot" style="background:${personColor(person.index)}" aria-hidden="true"></span>${escapeHtml(person.name)} <span class="krc-emoji">${escapeHtml(person.emojis[0]!.label)}</span><small>${formatCount(person.emojis[0]!.count)}번</small></li>`)
    .join('');
  const media = [
    totals.emoticon ? `이모티콘 ${formatCount(totals.emoticon)}개` : '',
    // A photo message may be an album ("사진 5장"), so this counts sends, not photos.
    totals.photo ? `사진 ${formatCount(totals.photo)}번` : '',
    totals.voice ? `음성메시지 ${formatCount(totals.voice)}개` : '',
    totals.link ? `링크 ${formatCount(totals.link)}개` : '',
  ].filter(Boolean).join(' · ');
  const tiles = top.length
    ? `<ol class="krc-emoji-tiles">${top.map((item, index) => `<li><span class="krc-emoji-rank">${index + 1}</span><span class="krc-emoji">${escapeHtml(item.label)}</span><small>${formatCount(item.count)}번</small></li>`).join('')}</ol>`
    : emptyNote('이모지는 거의 쓰지 않았어요. 담백한 대화파.');
  return card({
    id: 'emoji',
    title: '이모지 TOP',
    lead: top[0] ? `제일 많이 쓴 이모지는 ${top[0].label}, ${formatCount(top[0].count)}번이에요.` : undefined,
    body: `${tiles}${favorites ? `<p class="krc-sub-title">사람별 최애 이모지</p><ul class="krc-inline-list">${favorites}</ul>` : ''}${media ? `<p class="krc-media-line">${escapeHtml(media)}</p>` : ''}`,
  });
}

/** The room's longest ‘ㅋ’ run; a record two or more people share names them all. */
function laughLead(people: readonly PersonStats[]): string | undefined {
  const maxRun = (person: PersonStats) => person.laugh.maxRun;
  const champions = sharedBest(people, maxRun, formatCount, 'highest');
  const first = champions[0];
  if (!first || maxRun(first) < 2) return undefined;
  const record = `‘ㅋ’ ${formatCount(maxRun(first))}연타`;
  if (champions.length === 1) return `최장 기록은 ${first.name}의 ${record}.`;
  return `최장 기록은 ${record}. ${josa(sharedNames(champions), '이/가')} 나란히 세웠어요.`;
}

function laughCard(stats: ChatStats): string {
  const { shown, hidden } = busiestPeople(stats.people, (a, b) => b.laugh.rate - a.laugh.rate);
  const rows: BarRow[] = shown.map((person) => {
    const style = person.laugh.hCount > person.laugh.kCount ? 'ㅎ파' : 'ㅋ파';
    const details = person.laugh.messages > 0
      ? `${style} · ㅋ 평균 ${formatOneDecimal(person.laugh.avgRun)}개 · 최장 ${formatCount(person.laugh.maxRun)}연타${person.laugh.cryRuns ? ` · ㅠㅠ ${formatCount(person.laugh.cryRuns)}번` : ''}`
      : person.laugh.cryRuns ? `ㅠㅠ ${formatCount(person.laugh.cryRuns)}번` : '웃음 기록 없음';
    return {
      label: person.name,
      value: person.laugh.rate,
      valueText: `웃음 ${formatPercent(person.laugh.rate)}`,
      color: personColor(person.index),
      note: details,
      badge: personBadge(person),
    };
  });
  return card({
    id: 'laugh',
    title: '웃음 지문',
    lead: laughLead(stats.people),
    body: barRowsMarkup(rows, { max: 1 }) + hiddenPeopleNote(hidden),
    footnote: '웃음 = ㅋㅋ나 ㅎㅎ처럼 두 글자 이상 이어진 웃음이 들어간 메시지 비율이에요.',
  });
}

function catchphraseCard(stats: ChatStats): string {
  const people = peopleByMessages(stats.people).slice(0, MAX_PEOPLE_IN_CARD);
  const groups = people.map((person) => {
    const phrases = person.catchphrases.slice(0, 3);
    const chips = phrases.length
      ? phrases.map((item) => `<li class="krc-chip">‘${escapeHtml(item.phrase)}’ <small>${formatCount(item.count)}번</small></li>`).join('')
      : '<li class="krc-chip is-empty">아직 뚜렷한 말버릇이 없어요</li>';
    return `<div class="krc-phrase-group"><p class="krc-phrase-name"><span class="krc-dot" style="background:${personColor(person.index)}" aria-hidden="true"></span>${escapeHtml(person.name)}${person.isMe ? ' <span class="krc-me-badge">나</span>' : ''}</p><ul class="krc-chips">${chips}</ul></div>`;
  }).join('');
  const words = stats.topWords.slice(0, 10);
  return card({
    id: 'catchphrase',
    title: '말버릇',
    lead: '다른 사람보다 유난히 자주 쓰는 말이에요.',
    body: `${groups}${words.length ? `<p class="krc-sub-title">이 방에서 많이 나온 단어</p><ul class="krc-chips is-words">${words.map((word) => `<li class="krc-chip">${escapeHtml(word.label)} <small>${formatCount(word.count)}</small></li>`).join('')}</ul>` : ''}`,
    footnote: '말버릇은 그 사람이 다른 사람들보다 유난히 자주 쓴 단어예요. 많이 나온 단어는 방 전체 기준이에요.',
  });
}

function loveMorningCard(stats: ChatStats): string {
  const love = stats.love;
  const lovers = stats.people.filter((person) => person.love > 0).sort((a, b) => b.love - a.love);
  const morningPeople = stats.people.filter((person) => person.morningFirsts > 0).sort((a, b) => b.morningFirsts - a.morningFirsts);
  const loveBody = love.total > 0
    ? `<p class="krc-big-number"><strong>${formatCount(love.total)}</strong><span>번의 ‘사랑해’</span></p>
       <ul class="krc-facts">
         ${love.first ? `<li><span>처음</span>${escapeHtml(formatDate(love.first.at))} · ${escapeHtml(nameOf(stats, love.first.person))}</li>` : ''}
         ${love.last ? `<li><span>최근</span>${escapeHtml(formatDate(love.last.at))} · ${escapeHtml(nameOf(stats, love.last.person))}</li>` : ''}
         ${lovers.length ? `<li><span>많이 말한 사람</span>${escapeHtml(lovers.slice(0, 3).map((person) => `${person.name} ${formatCount(person.love)}번`).join(' · '))}</li>` : ''}
       </ul>`
    : emptyNote('아직 ‘사랑해’는 나오지 않았어요.');
  const morningBody = stats.morning.days > 0
    ? `<p class="krc-big-number"><strong>${formatCount(stats.morning.days)}</strong><span>일 동안 아침 인사</span></p>
       ${morningPeople.length ? `<ul class="krc-facts"><li><span>먼저 깨운 사람</span>${escapeHtml(morningPeople.slice(0, 3).map((person) => `${person.name} ${formatCount(person.morningFirsts)}일`).join(' · '))}</li></ul>` : ''}`
    : emptyNote('‘좋은 아침’ 같은 아침 인사는 아직 없었어요.');
  return card({
    id: 'love-morning',
    title: '사랑해 · 좋은 아침',
    body: `<div class="krc-split"><div><p class="krc-sub-title">사랑해</p>${loveBody}</div><div><p class="krc-sub-title">좋은 아침</p>${morningBody}</div></div>`,
    footnote: '‘사랑해’는 사랑해·사랑합니다·love you 같은 표현을, 아침 인사는 낮 12시 전의 좋은 아침·굿모닝·잘 잤어 같은 말을 셌어요.',
  });
}

function burstCard(stats: ChatStats): string {
  const byLongestRun: PersonOrder = (a, b) => b.burst.max - a.burst.max || b.burst.average - a.burst.average;
  const { shown, hidden } = busiestPeople(stats.people, byLongestRun);
  const rows: BarRow[] = shown.map((person) => ({
    label: person.name,
    value: person.burst.average,
    valueText: `평균 ${formatOneDecimal(person.burst.average)}개씩`,
    color: personColor(person.index),
    note: person.burst.max > 1
      ? `최대 ${formatCount(person.burst.max)}개 연속${person.burst.maxAt !== null ? ` (${formatDate(person.burst.maxAt)})` : ''}`
      : '한 번에 하나씩 보내는 편',
    badge: personBadge(person),
  }));
  const top = leaderBy(stats.people, byLongestRun);
  return card({
    id: 'burst',
    title: '연타 기록',
    lead: top && top.burst.max >= 3 ? `${josa(top.name, '은/는')} 답을 기다리지 않고 최대 ${formatCount(top.burst.max)}개를 연달아 보냈어요.` : undefined,
    body: barRowsMarkup(rows) + hiddenPeopleNote(hidden),
    footnote: '다른 사람이 말하기 전까지 혼자 이어 보낸 메시지 수예요.',
  });
}

function lengthCard(stats: ChatStats): string {
  const { shown, hidden } = busiestPeople(stats.people, (a, b) => b.avgTextLength - a.avgTextLength);
  const rows: BarRow[] = shown.map((person) => ({
    label: person.name,
    value: person.avgTextLength,
    valueText: `평균 ${formatOneDecimal(person.avgTextLength)}자`,
    color: personColor(person.index),
    note: `${SHORT_REPLY_MAX_CHARS}글자 이하 ${formatPercent(person.shortReplyRate)} · 물음표로 끝남 ${formatPercent(person.questionRate)}`,
    badge: personBadge(person),
  }));
  const longest = stats.longestMessage;
  const longestMarkup = longest
    ? `<figure class="krc-quote"><figcaption>가장 긴 메시지 · ${escapeHtml(nameOf(stats, longest.person))} · ${formatCount(longest.length)}자 · ${escapeHtml(formatDate(longest.at))}</figcaption><blockquote>${escapeHtml(longest.excerpt)}</blockquote></figure>`
    : '';
  return card({
    id: 'length',
    title: '장문 vs 단답',
    body: barRowsMarkup(rows) + hiddenPeopleNote(hidden) + longestMarkup,
    footnote: '글자 수는 공백을 빼고 셌어요. 사진·이모티콘은 빼고 글 메시지만 봤어요.',
  });
}

function calendarCard(stats: ChatStats): string {
  const model = buildHeatmap(stats.calendar, stats.totals.firstAt, stats.totals.lastAt);
  return card({
    id: 'calendar',
    title: '대화 달력',
    lead: `${formatCount(stats.totals.spanDays)}일 중 ${formatCount(stats.totals.activeDays)}일 대화했어요.`,
    body: heatmapMarkup(model),
    footnote: `최근 ${HEATMAP_WEEKS}주(약 1년)까지 보여 줘요. 진할수록 메시지가 많은 날이에요.`,
    wide: true,
  });
}

function recordsCard(stats: ChatStats): string {
  const items: string[] = [];
  if (stats.recordDay) {
    items.push(`<div><dt>기록의 날</dt><dd><strong>${escapeHtml(formatDate(dayKeyToMs(stats.recordDay.date)))}</strong><span>하루에 메시지 ${formatCount(stats.recordDay.count)}개</span></dd></div>`);
  }
  if (stats.longestStreak) {
    items.push(`<div><dt>최장 연속 대화</dt><dd><strong>${formatCount(stats.longestStreak.days)}일</strong><span>${escapeHtml(formatPeriod(dayKeyToMs(stats.longestStreak.from), dayKeyToMs(stats.longestStreak.to)))}</span></dd></div>`);
  }
  if (stats.longestSilence) {
    items.push(`<div><dt>가장 긴 침묵</dt><dd><strong>${escapeHtml(formatDuration(stats.longestSilence.minutes))}</strong><span>${escapeHtml(formatPeriod(stats.longestSilence.from, stats.longestSilence.to))}</span></dd></div>`);
  }
  items.push(`<div><dt>대화 횟수</dt><dd><strong>${formatCount(stats.totals.conversations)}번</strong><span>한 번에 평균 ${formatAverage(stats.totals.avgConversationMessages)}개</span></dd></div>`);
  items.push(`<div><dt>대화한 날</dt><dd><strong>${formatCount(stats.totals.activeDays)}일</strong><span>대화한 날 하루 평균 ${formatAverage(stats.totals.perActiveDay)}개</span></dd></div>`);
  return card({ id: 'records', title: '기록실', body: `<dl class="krc-records">${items.join('')}</dl>` });
}

function snippetMarkup(stats: ChatStats, snippet: MessageSnippet): string {
  const person = stats.people.find((item) => item.index === snippet.person);
  const kindLabel = KIND_LABELS[snippet.kind];
  const text = snippet.kind === 'text' ? escapeHtml(snippet.text) : `<span class="krc-media-tag">${escapeHtml(kindLabel)}</span>`;
  return `<li class="krc-bubble" style="--krc-person:${personColor(snippet.person)}"><p class="krc-bubble-meta"><strong>${escapeHtml(person?.name ?? '')}</strong> <time>${escapeHtml(`${formatDate(snippet.at)} ${formatTime(snippet.at)}`)}</time></p><p class="krc-bubble-text">${text}</p></li>`;
}

function snippetsCard(stats: ChatStats): string {
  if (!stats.firstMessages.length && !stats.lastMessages.length) return '';
  return card({
    id: 'snippets',
    title: '처음과 지금',
    body: `<div class="krc-split">
        <div><p class="krc-sub-title">처음 나눈 메시지</p><ol class="krc-bubbles">${stats.firstMessages.map((snippet) => snippetMarkup(stats, snippet)).join('')}</ol></div>
        <div><p class="krc-sub-title">최근 메시지</p><ol class="krc-bubbles">${stats.lastMessages.map((snippet) => snippetMarkup(stats, snippet)).join('')}</ol></div>
      </div>`,
    footnote: '이 화면은 이 기기에서만 보여요. 저장 이미지에는 메시지 내용이 들어가지 않아요.',
  });
}

/** Wide cards first (hourly, calendar), then the column cards. */
export function statCardsMarkup(stats: ChatStats): { readonly wide: string; readonly columns: string } {
  return {
    wide: [hourlyCard(stats), calendarCard(stats)].join(''),
    columns: [
      talkShareCard(stats),
      initiativeCard(stats),
      replyCard(stats),
      lateNightCard(stats),
      weekdayCard(stats),
      emojiCard(stats),
      laughCard(stats),
      catchphraseCard(stats),
      loveMorningCard(stats),
      burstCard(stats),
      lengthCard(stats),
      recordsCard(stats),
      snippetsCard(stats),
    ].join(''),
  };
}
